import React, { useMemo, useRef } from 'react';
import { AriaTabListProps, useTabList } from 'react-aria';
import { TabListProps, useTabListState } from 'react-stately';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import {
  TAB_PANEL_CLASSNAME,
  TAB_PANEL_TRANSITION_DURATION_MILLIS,
} from './constants';
import * as S from './styles';
import Tab from './Tab';
import TabItem from './TabItem';

export interface TabsProps<T extends object>
  extends TabListProps<T>,
    AriaTabListProps<T> {
  className?: string;
  tabPanelClassName?: string;
}

export const TAB_PANE_CLASSNAME = 'BlockeadTabs__Pane';

/**
 * An accessible Tabs component.
 *
 * This component is build on top of hooks from react-aria, namely `useTab`,
 * `useTabList` and `useTabPanel` which can all be found <a href="https://react-spectrum.adobe.com/react-aria/useTabList.html" target="_blank">here</a>.
 *
 * One design limitation of using these hooks is that they expect there to be a single
 * tab panel which renders different contents based on the selected tab.  This can make
 * a few patterns difficult:
 * <ul>
 *  <li>
 *    If one or more tabs have a scrollable body, then restoring scroll position when
 *    navigating between tabs has to be implemented manually
 *  </li>
 *  <li>
 *    Since a tab is unmounted when it is deselected, it might be difficult or
 *    impossible to restore state in a performant way.  For example, a tab might
 *    have an audio player and deselecting the tab means that the audio object would
 *    get unmounted and playback would stop.  Also, if the audio player was expensive
 *    to initialize, the user would have to pay the cost of that initialization every
 *    time they switched back to that tab
 *  </li>
 * </ul>
 */
// TODO: If limitations outlined above become a problem, consider switching to a different
// tab library.  There are popular tabs libraries out there that also support accessibility
function Tabs<T extends object>(
  props: TabsProps<T>,
): React.ReactElement | null {
  const { className } = props;

  const tabListRef = useRef<HTMLDivElement>(null);
  const tabPanelRef = useRef<HTMLDivElement>(null);
  const state = useTabListState(props);
  const items = useMemo(() => Array.from(state.collection), [state.collection]);
  const { tabListProps } = useTabList(props, state, tabListRef);

  const selectedIndex = useMemo(
    () => items.indexOf(state.selectedItem),
    [items, state.selectedItem],
  );

  return (
    <div className={className}>
      <S.TabList {...tabListProps} ref={tabListRef}>
        <S.Indicator
          $selectedIndex={selectedIndex}
          $tabCount={state.collection.size}
        />
        {items.map((item) => (
          <Tab key={item.key} {...{ item, state }} />
        ))}
      </S.TabList>
      <SwitchTransition mode="out-in">
        <CSSTransition
          addEndListener={(done) =>
            tabPanelRef.current?.addEventListener('transitionend', done, false)
          }
          classNames={TAB_PANEL_CLASSNAME}
          key={state.selectedItem?.key}
          nodeRef={tabPanelRef}
          timeout={TAB_PANEL_TRANSITION_DURATION_MILLIS}
        >
          <S.TabPanel
            className={TAB_PANE_CLASSNAME}
            ref={tabPanelRef}
            state={state}
          />
        </CSSTransition>
      </SwitchTransition>
    </div>
  );
}

Tabs.Item = TabItem;

export default Tabs;
